import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { env } from '../../../config/env';
import { useSesionStore } from '../../stores/sesionStore';
import { Logo } from './Logo';
import { LogoDeCliente } from '../../pages/inventario/LogoDeCliente';
import estilos from './MarcaLateral.module.css';

/**
 * Marca en la cabecera de la barra lateral, igual que en la versión anterior:
 *
 *   1. El logotipo de VenIoT, siempre. Si el cliente tiene logo propio, VenIoT
 *      pasa a su versión horizontal (más baja) para dejarle el sitio.
 *   2. Debajo, el logotipo del cliente, si lo tiene.
 *   3. Debajo, «Superadmin» o el nombre del cliente.
 *
 * VenIoT nunca desaparece: es la marca del producto; la del cliente se suma,
 * no lo sustituye.
 *
 * Triple pulsación sobre el logotipo de VenIoT: abre el formulario para
 * cambiar el logo del cliente, igual que en la versión anterior. Lo pueden
 * usar el superadministrador y el administrador del cliente, siempre que la
 * sesión pertenezca a un cliente concreto.
 */
export function MarcaLateral() {
  const perfil = useSesionStore((s) => s.perfil);
  const esSuperadmin = perfil?.rol.esSuperadmin ?? false;
  const clienteId = perfil?.clienteId ?? null;

  const [falloLogo, setFalloLogo] = useState(false);
  const [editorAbierto, setEditorAbierto] = useState(false);

  // Marcas de tiempo de las últimas pulsaciones, para detectar la triple.
  const pulsaciones = useRef<number[]>([]);

  const puedeEditarLogo =
    clienteId !== null && (esSuperadmin || (perfil?.rol.esAdminCliente ?? false));

  const alPulsarLogo = () => {
    if (!puedeEditarLogo) return;

    const ahora = Date.now();
    pulsaciones.current = pulsaciones.current.filter((t) => ahora - t <= 600);
    pulsaciones.current.push(ahora);

    if (pulsaciones.current.length >= 3) {
      pulsaciones.current = [];
      setEditorAbierto(true);
    }
  };

  const logo = useQuery({
    queryKey: ['cliente', 'logo', clienteId],
    queryFn: () => repositorioCatalogos.logoDeCliente(clienteId),
    enabled: clienteId !== null,
    staleTime: 30 * 60_000,
  });

  // El nombre del cliente para el rótulo; la lista ya viene acotada por RLS.
  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    enabled: !esSuperadmin && clienteId !== null,
    staleTime: 5 * 60_000,
  });

  // Un logo recién subido merece volver a intentarse aunque el anterior fallara.
  useEffect(() => {
    setFalloLogo(false);
  }, [logo.data]);

  const urlLogoCliente = !falloLogo ? (logo.data ?? null) : null;
  const nombreCliente = clientes.data?.find((c) => c.id === clienteId)?.nombre ?? null;

  const rotulo = esSuperadmin ? 'Superadmin' : (nombreCliente ?? env.marca.nombreApp);

  return (
    <>
      <span
        className={puedeEditarLogo ? estilos.zonaEditable : undefined}
        title={puedeEditarLogo ? 'Triple pulsación para cambiar el logo del cliente' : undefined}
        onClick={alPulsarLogo}
      >
        <Logo
          variante={urlLogoCliente ? 'largo' : 'centrado'}
          ancho={160}
          className={estilos.logoVeniot}
        />
      </span>

      {urlLogoCliente && (
        <img
          src={urlLogoCliente}
          alt={nombreCliente ?? 'Logotipo del cliente'}
          className={estilos.logoCliente}
          onError={() => setFalloLogo(true)}
        />
      )}

      <h1 className={estilos.rotulo}>{rotulo}</h1>

      <LogoDeCliente
        abierto={editorAbierto}
        clienteId={clienteId}
        clienteNombre={nombreCliente ?? rotulo}
        onCerrar={() => setEditorAbierto(false)}
      />
    </>
  );
}
